import { getPublicTopic } from '../../topic/@shared/public-topic.helper';
import { OrganizationTopic } from './organization-topic.entity';
import {
  OrganizationContactPerson, WhitelistedField
} from "@api/organization/@shared/organization-contact-person.entity";
import {
  Organization, OrganizationEmail,
  OrganizationSubType,
} from "@api/organization/@shared/organization.entity";
import { publicFieldsConfig } from "@api/organization/@shared/public-fields.config";
import { OrganizationType } from "@lib/types";

const PUBLIC_FIELDS_CONFIG = publicFieldsConfig;
const ORGANIZATION_PROPERTY_CONTACT_PERSONS = 'contactPersons';
const ORGANIZATION_PROPERTY_EMAILS = 'emails';
const ORGANIZATION_PROPERTY_LOCAL_CONTACT_POINT = 'localContactPoint';
const ORGANIZATION_PROPERTY_CONTACT_ORGANIZATION = 'contactOrganization';
const ORGANIZATION_PROPERTY_BRANCH_OFFICES = 'branchOffices';
const ORGANIZATION_PROPERTY_MAIN_OFFICE = 'mainOffice';
const MAX_PUBLIC_CONTACT_PERSONS = 1;
export const PUBLIC_UNPUBLISHED_ORGANIZATION_DETAIL_FIELDS: (keyof Organization)[] = [
  'id',
  'type',
  'createdAt',
  'updatedAt',
  'subType',
  'subTypeForm',
  'name',
];
export const PUBLIC_CO_ORGANIZATION_RELATION_FIELDS: (keyof Organization)[] = [
  'name',
  'addressStreetAndNumber',
  'addressPostalCode',
  'addressCity',
  'addressFederalState',
  'phoneContacts',
  'emails',
  'websites',
  'socialMediaLinks',
];
const CONTACT_VIA_NAKOS_HIDDEN_FIELDS: (keyof Organization)[] = [
  'contactPersons'
]

export const SANITIZABLE_FIELDS: Set<keyof Organization> = new Set([
  ORGANIZATION_PROPERTY_CONTACT_PERSONS,
  ORGANIZATION_PROPERTY_EMAILS,
  ORGANIZATION_PROPERTY_LOCAL_CONTACT_POINT,
  ORGANIZATION_PROPERTY_CONTACT_ORGANIZATION,
  ORGANIZATION_PROPERTY_BRANCH_OFFICES,
  ORGANIZATION_PROPERTY_MAIN_OFFICE,
  'topics',
]);

export function getPublicOrganization(organization: Organization): Partial<Organization> {
  const publicFieldIds = getPublicOrganizationFieldIds(organization.type, organization.subType);

  if (!publicFieldIds) {
    return null;
  }

  const publicOrganization: Partial<Organization> = {};
  const shouldHideOwnContactFields = shouldHideContactViaNakosFields(organization);

  for (const fieldId of publicFieldIds.keys()) {
    // hide own contact information EXCEPT for the name (that is still needed on detail pages)
    if (shouldHideOwnContactFields
      && PUBLIC_CO_ORGANIZATION_RELATION_FIELDS.includes(fieldId)
      && fieldId !== 'name') {
      continue;
    }

    // these fields are not replaced by the co-organization relation, but should still be hidden if contactViaNakos is true
    if (shouldHideOwnContactFields && CONTACT_VIA_NAKOS_HIDDEN_FIELDS.includes(fieldId)) {
      continue;
    }

    if (!organization.hasOwnProperty(fieldId)) {
      // console.warn(`Field "${fieldId}" is listed in public fields configuration but does not exist on organization entity.`);
      continue;
    }

    if (SANITIZABLE_FIELDS.has(fieldId)) {
      (publicOrganization[fieldId] as any) = sanitizeValue(organization[fieldId], fieldId);

      continue;
    }

    // Using type-assertion to silence the compiler, as we know that fieldId is a key of Organization
    (publicOrganization as any)[fieldId] = organization[fieldId];
  }

  return publicOrganization;
}

export function getPublicUnpublishedOrganizationDetail(organization: Organization): Partial<Organization> {
  if (!organization) {
    return null;
  }

  const publicOrganization: Partial<Organization> = {};

  for (const fieldId of PUBLIC_UNPUBLISHED_ORGANIZATION_DETAIL_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(organization, fieldId)) {
      continue;
    }

    (publicOrganization as any)[fieldId] = organization[fieldId];
  }

  return publicOrganization;
}

export function sanitizeValue(value: any, fieldId: keyof Organization): any {
  if (!SANITIZABLE_FIELDS.has(fieldId)) {
    return value;
  }

  switch (fieldId) {
    case 'topics':
      return ((value || []) as OrganizationTopic[]).map(relation => ({
        organizationId: relation.organizationId,
        topicId: relation.topicId,
        position: relation.position,
        ...(relation.topic ? { topic: getPublicTopic(relation.topic) } : {}),
      }));

    case ORGANIZATION_PROPERTY_CONTACT_PERSONS:
      return (value as OrganizationContactPerson[]).slice(0, MAX_PUBLIC_CONTACT_PERSONS).map((contactPerson) => {
        return sanitizeContactPerson(contactPerson);
      }).filter((value) => {
        return !isEmpty(value);
      });

    case ORGANIZATION_PROPERTY_EMAILS:
      return (value as OrganizationEmail[]).map((email) => {
        return sanitizeEmail(email);
      }).filter((value) => {
        return !isEmpty(value);
      });

    case ORGANIZATION_PROPERTY_LOCAL_CONTACT_POINT:
    case ORGANIZATION_PROPERTY_CONTACT_ORGANIZATION:
      return sanitizeContactViaRelation(value as Organization);

    case ORGANIZATION_PROPERTY_MAIN_OFFICE:
      return sanitizeOrganizationRelation(value as Organization);

    case ORGANIZATION_PROPERTY_BRANCH_OFFICES:
      return (value as Organization[])
        .map((office) => sanitizeOrganizationRelation(office))
        .filter((office): office is Partial<Organization> => office !== null);

    default:
      return value;
  }
}

export function getPublicOrganizationFieldIds(type: OrganizationType, subType?: OrganizationSubType):
  Set<keyof Organization> {
  const configKey = subType || type;
  const publicFieldIds = PUBLIC_FIELDS_CONFIG[configKey] as (keyof Organization)[];

  if (!publicFieldIds) {
    console.error(`No public fields configuration found for organization with subType/type: ${configKey}`);

    return null;
  }

  return new Set(publicFieldIds);
}

export function sanitizeContactPerson(contactPerson: OrganizationContactPerson): Partial<OrganizationContactPerson> {
  if (!contactPerson) {
    return null;
  }

  const sanitizedContactPerson: { [key in WhitelistedField]: string } = {
    name: contactPerson.person?.name || null,
    phoneNumber: contactPerson.phoneNumber || null,
    email: contactPerson.email || null,
  }

  if (!contactPerson.hiddenFields || !contactPerson.hiddenFields.length) {
    return sanitizedContactPerson;
  }

  for (const field of contactPerson.hiddenFields) {
    delete sanitizedContactPerson[field];
  }

  return sanitizedContactPerson;
}

export function sanitizeEmail(email: OrganizationEmail | string): string {
  if (!email) {
    return null;
  }

  if (typeof email === 'string') {
    return email;
  }

  return email.email;
}

export function sanitizeOrganizationRelation(organization: Organization): Partial<Organization> {
  if (!organization) {
    return null;
  }

  if (organization.isPublished !== true) {
    return null;
  }

  const publicOrganization = getPublicOrganization(organization);

  if (!publicOrganization) {
    return null;
  }

  const sanitizedOrganization: Partial<Organization> = {};

  for (const fieldId of PUBLIC_CO_ORGANIZATION_RELATION_FIELDS) {
    if (!Object.prototype.hasOwnProperty.call(publicOrganization, fieldId)) {
      continue;
    }

    (sanitizedOrganization as any)[fieldId] = publicOrganization[fieldId];
  }

  return sanitizedOrganization;
}

function sanitizeContactViaRelation(organization: Organization): Partial<Organization> {
  if (!organization) {
    return null;
  }

  // Only an explicit contact-via relation grants this narrow view. The
  // standalone otherAddress type has no public detail configuration.
  const allowedFields = organization.type === 'otherAddress'
    ? new Set(PUBLIC_CO_ORGANIZATION_RELATION_FIELDS)
    : getPublicOrganizationFieldIds(organization.type, organization.subType);
  if (!allowedFields) {
    return null;
  }

  const sanitizedOrganization: Partial<Organization> = {};
  for (const fieldId of PUBLIC_CO_ORGANIZATION_RELATION_FIELDS) {
    if (!allowedFields.has(fieldId)
      || (shouldHideContactViaNakosFields(organization) && fieldId !== 'name')
      || !Object.prototype.hasOwnProperty.call(organization, fieldId)) {
      continue;
    }

    if (fieldId === 'emails') {
      sanitizedOrganization.emails = (organization.emails || [])
        .map(sanitizeEmail)
        .filter((email) => !isEmpty(email)) as any;
    } else if (fieldId === 'phoneContacts') {
      sanitizedOrganization.phoneContacts = (organization.phoneContacts || [])
        .map(({ number, officeHours }) => ({ number, officeHours }));
    } else {
      (sanitizedOrganization as any)[fieldId] = organization[fieldId];
    }
  }

  return sanitizedOrganization;
}

function shouldHideContactViaNakosFields(organization: Organization): boolean {
  return organization.subType === 'selfHelpAddressBa' && organization.contactViaNakos === true;
}

export function isEmpty(value: unknown): boolean {
  // eslint-disable-next-line no-undefined
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === 'string') {
    return value.trim() === '';
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  if (typeof value === 'object') {
    return Object.keys(value).length === 0;
  }

  // All other values (numbers, booleans, functions, symbols, etc.) are not empty
  return false;
}
